import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MoreHorizontal, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { useSidebarStore } from 'store/sidebar.store';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from 'components/ui/dropdown-menu';
import { useStudioNavGroups, type StudioNavItem } from './LeftSidebarContent';
import { useCreateActions, type CreateAction } from './HeaderCreateMenu';

/** A single bottom-bar destination tab. */
function Tab({ item }: { item: StudioNavItem }) {
  return (
    <Link
      to={item.to}
      aria-current={item.active ? 'page' : undefined}
      className={cn(
        'relative flex flex-1 flex-col items-center justify-center gap-1 py-2 text-[10px] font-medium transition-colors',
        item.active ? 'text-primary-700 dark:text-primary-300' : 'text-muted-foreground',
      )}
    >
      {item.active && (
        <span className="absolute inset-x-4 top-0 h-0.5 rounded-full bg-primary" aria-hidden />
      )}
      <span className="relative">
        <item.icon className="h-5 w-5" aria-hidden />
        {item.badge != null && item.badge > 0 && (
          <span className="absolute -right-1.5 -top-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-error-600 px-1 text-[9px] font-bold leading-none text-white">
            {item.badge}
          </span>
        )}
      </span>
      <span className="max-w-full truncate px-0.5">{item.label}</span>
    </Link>
  );
}

/** Hoisted to module scope — declared inline it remounted its Radix menu on every unrelated re-render. */
function CreateFab({ actions }: { actions: CreateAction[] }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const fab = (
    <span className="-mt-3.5 flex h-[52px] w-[52px] items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-md">
      <Plus className="h-6 w-6" aria-hidden />
    </span>
  );

  if (actions.length === 1) {
    return (
      <button
        type="button"
        onClick={() => navigate(actions[0].to)}
        aria-label={actions[0].label}
        className="flex flex-1 items-start justify-center"
      >
        {fab}
      </button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('create', 'Create')}
          className="flex flex-1 items-start justify-center"
        >
          {fab}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="center" sideOffset={12} className="w-56">
        {actions.map((a) => (
          <DropdownMenuItem key={a.to} onClick={() => navigate(a.to)} className="gap-2.5">
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
              <a.icon className="h-4 w-4" aria-hidden />
            </span>
            <span className="text-sm">{a.label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Only ever mounted at >=768px (MobileShell handles phones), so in practice it renders nothing today. */
export function MobileTabBar() {
  const { t } = useTranslation();
  const { toggleOpen } = useSidebarStore();
  const { groups, isFetched } = useStudioNavGroups();
  const actions = useCreateActions();

  if (!isFetched) return null;

  // Real route destinations only — dialog targets (?settings=) live in "More".
  const dest = groups.flatMap((g) => g.items).filter((item) => !item.to.startsWith('?'));
  const canCreate = actions.length > 0;
  // With the FAB present, budget 3 tabs (2 left · 1 right) so the row stays at
  // five thumb targets; without it, keep the original four.
  const tabs = dest.slice(0, canCreate ? 3 : 4);
  const left = canCreate ? tabs.slice(0, 2) : tabs;
  const right = canCreate ? tabs.slice(2) : [];

  return (
    <nav
      aria-label={t('primary_navigation', 'Primary navigation')}
      className="fixed inset-x-0 bottom-0 z-30 flex items-stretch border-t border-border bg-background/95 backdrop-blur-xl md:hidden"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {left.map((item) => (
        <Tab key={item.to} item={item} />
      ))}

      {canCreate && <CreateFab actions={actions} />}

      {right.map((item) => (
        <Tab key={item.to} item={item} />
      ))}

      <button
        type="button"
        onClick={toggleOpen}
        aria-label={t('more', 'More')}
        className="flex flex-1 flex-col items-center justify-center gap-1 py-2 text-[10px] font-medium text-muted-foreground transition-colors"
      >
        <MoreHorizontal className="h-5 w-5" aria-hidden />
        <span>{t('more', 'More')}</span>
      </button>
    </nav>
  );
}

export default MobileTabBar;
